import { useEffect, useMemo, useState } from 'react'
import { Trans } from 'react-i18next'
import clsx from 'clsx'

import { GetImageMetdataLink } from '@makyoapp/makyo-sdk'
import { ErrorResponse } from '@makyoapp/makyo-sdk/api/api'
import { ImageMetadata } from '@makyoapp/makyo-sdk/models/Files'
import * as Icon from '@makyoapp/makyo-icon/icon'
import { Button, Field, SelectDropdown, Slider, Switch } from '@makyoapp/makyo-ui'

import { NavbarSettings, NavPosition, NavSpacing } from '@/adapters/__project__/BuilderNavbarAdapter'
import { useAppBrandKitContext } from '@/context/AppBrandKitContext'
import { useBuilderColorSchemeV2 } from '@/hooks/__project__/useBuilderColorSchemeV2'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { useReqError } from '@/hooks/useReqError'
import { MdShape } from '@/shared/builder/types'
import { navbarConfigGroups, NavbarConfigGroups, NavDisplayOption } from '@/shared/main-navigation-bar'

import { SliderFieldSetting } from '../ComponentPanel/FieldSetting'

const positionOptions: NavPosition[] = ['none', 'bottom', 'top', 'leading']
const spacingOptions: NavSpacing[] = ['close', 'spread']

const ToggleField = ({
  label,
  options,
  selected,
  onSelect,
}: {
  label: string
  options: { title?: string; name: string; icon: JSX.Element }[]
  selected: string
  onSelect: (name: string) => void
}) => (
  <Field isVertical className='py-0 flex items-center! flex-row! justify-between! min-h-10!'>
    <label>{label}</label>
    <div className='flex items-center gap-2'>
      {options.map(({ title, name, icon }) => (
        <Button
          key={name}
          onClick={() => onSelect(name)}
          squared
          noPadding
          title={title ?? name}
          className={clsx('p-2', selected !== name && 'bg-slate-300 text-slate-700')}
        >
          {icon}
        </Button>
      ))}
    </div>
  </Field>
)
interface NavbarItemSettingsProps {
  settings: Partial<NavbarSettings>
  onFieldChange: (field: string, value: any, setting?: string) => void
  onSwitchChange: (name: string, checked: boolean, setting?: string, action?: string) => void
  handleApplyAllChanges: () => void
  isApplying: boolean
  ModeIcon: JSX.Element
}

export const NavbarItemSettings = ({
  settings,
  onFieldChange,
  onSwitchChange,
  handleApplyAllChanges,
  isApplying,
  ModeIcon,
}: NavbarItemSettingsProps) => {
  const {
    position,
    spacing,
    iconDirection,
    logoAlignment,
    menuAlignment,
    menuHeight,
    menuWidth,
    logoWidth,
    logo,
    logoDark,
    cornerRadius,
    displayOptions,
    maxItems,
  } = settings

  const [scheme] = useBuilderColorSchemeV2()
  const mode = scheme.current

  const activeLogo = mode === 'dark' ? logoDark : logo

  const findKeyByValue = (configGroups: NavbarConfigGroups, valueToFind: any) => {
    return Object.keys(configGroups).find((key) => configGroups[key as keyof NavbarConfigGroups].includes(valueToFind))
  }

  const printAPIError = useReqError()

  const [refreshKey, setRefreshKey] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  const { isPhonePortrait, isDesktop, isTV } = useBreakpoint()
  const { brandLogos, activeBrandKit, uploadLogoBrandkit } = useAppBrandKitContext()

  const isNavPositionLeading = position === 'leading'
  const isNavigationBarTop = position === 'top'

  const validateSliderValue = (value: string | number, min: number, max: number): number => {
    const parsedValue = value ? parseInt(value.toString()) : 50
    if (parsedValue > max) return max
    if (parsedValue < min) return min
    return parsedValue ?? 50
  }

  useEffect(() => {
    if (brandLogos.length > 0 && activeLogo && activeLogo.value) {
      if (!brandLogos.some((b) => b.value === activeLogo.value)) {
        onFieldChange(mode === 'dark' ? 'logoDark' : 'logo', brandLogos[0].value)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandLogos, activeLogo, mode])

  const filteredSwitchFields = useMemo(() => {
    const filteredFields = displayOptions?.filter((field) => field.name !== 'show_logo') || ([] as NavDisplayOption[])

    return filteredFields.filter(
      (field) =>
        field.name === 'show_shadow' ||
        field.name === 'show_icon' ||
        field.name === 'show_label' ||
        field.name === 'show_hover' ||
        field.name === 'show_active_indicator' ||
        field.name === 'sticky'
    )
  }, [displayOptions])

  const isShowMaxItems = isDesktop || isTV

  const noLogoOption = (
    <button
      className='text-xs font-normal flex items-center gap-1'
      onClick={() => onSwitchChange('show_logo', false, 'logo', 'removeLogo')}
    >
      <Icon.Block16 className='w-6 h-6' />
      <p className='text-xs font-normal'>No Logo</p>
    </button>
  )

  const addNewLogoOption = (
    <label className={clsx('cursor-pointer gap-1 flex items-center')}>
      <Icon.PlusCircleLine16 className='w-6 h-6' />
      <input
        type='file'
        className='sr-only'
        accept='.jpeg,.jpg,.png,.webm,.webp,.svg'
        disabled={isLoading}
        onChange={async (e) => {
          const file = e.target.files?.[0]
          if (file && activeBrandKit) {
            const split = file.name.split('.')
            const ext = split[split.length - 1]
            setIsLoading(true)
            try {
              const uploadedLogo = await uploadLogoBrandkit(activeBrandKit.id, file, ext)

              const timeout = setTimeout(() => {
                const newLogo = {
                  value: uploadedLogo._File,
                  label: file.name,
                }
                onFieldChange(mode === 'dark' ? 'logoDark' : 'logo', newLogo, 'logo')
                setRefreshKey((prev) => prev + 1)
              }, 1500)
              return () => clearTimeout(timeout)
            } catch (err) {
              printAPIError(err as string | Error | ErrorResponse)
            } finally {
              setIsLoading(false)
            }
          }
        }}
      />
      <p className='text-xs font-normal'>Add New Logo</p>
    </label>
  )

  return (
    <>
      <p className='text-xs font-medium mt-2 text-teal-400'>MENU</p>
      <div className='flex flex-col gap-2 mt-4'>
        {!isPhonePortrait && (
          <Field isVertical className='py-0 flex items-center! -mt-3'>
            <label className='w-full'>
              <Trans>Position</Trans>
            </label>
            <div className='w-full flex flex-col gap-0'>
              <SelectDropdown
                className='dark:bg-slate-700/90'
                id='position'
                options={positionOptions.filter((pos) => {
                  if (isPhonePortrait) {
                    return pos === 'bottom'
                  }
                  return pos
                })}
                renderOption={(opt: NavPosition) => (
                  <div className='flex items-center gap-1'>
                    <p className='text-xs font-normal capitalize'>{opt}</p>
                  </div>
                )}
                value={position}
                onChange={(value) => onFieldChange('position', value, 'menu')}
              />
            </div>
          </Field>
        )}
        {/* <Field isVertical className='py-0 flex items-center! -mt-3'>
          <label className='w-full'>
            <Trans>Spacing</Trans>
          </label>
          <div className='w-full flex flex-col gap-0'>
            <SelectDropdown
              className='dark:bg-slate-700/90'
              id='position'
              options={spacingOptions}
              renderOption={(opt: NavSpacing) => (
                <div className='flex items-center gap-1'>
                  <p className='text-xs font-normal capitalize'>{opt}</p>
                </div>
              )}
              value={spacing}
              onChange={(value) => onFieldChange('spacing', value, 'menu')}
            />
          </div>
        </Field> */}
        <div className='flex gap-6'>
          <div>
            <label className='w-full'>
              <Trans>Orientation</Trans>
            </label>
            <div className='w-fit -ml-2'>
              <ToggleField
                label=''
                options={[
                  { title: 'Horizontal', name: 'horizontal', icon: <Icon.Horizontal24 /> },
                  { title: 'Vertical', name: 'vertical', icon: <Icon.Vertical24 /> },
                ]}
                selected={iconDirection ?? 'horizontal'}
                onSelect={(name) => onFieldChange('iconDirection', name, 'menu')}
              />
            </div>
          </div>
          <div>
            <label className='w-full'>
              <Trans>Alignment</Trans>
            </label>
            <div className='w-fit -ml-2'>
              <ToggleField
                label=''
                options={[
                  { title: 'Leading', name: 'leading', icon: <Icon.AlignLeft16 /> },
                  { title: 'Center', name: 'center', icon: <Icon.AlignCenter16 /> },
                  { title: 'Trailing', name: 'trailing', icon: <Icon.AlignRight16 /> },
                ]}
                selected={menuAlignment ?? 'leading'}
                onSelect={(name) => onFieldChange('menuAlignment', name, 'menu')}
              />
            </div>
          </div>
        </div>

        {!isNavPositionLeading && (
          <Field isVertical className='py-0 flex items-center!'>
            <label className='w-full -mb-2'>
              <Trans>Height</Trans>
            </label>
            <div className='w-full flex flex-col gap-0'>
              <p className='text-xs text-light flex justify-end'>{menuHeight}</p>
              <Slider
                values={[validateSliderValue(menuHeight ?? '', 50, 200)]}
                step={5}
                min={50}
                max={200}
                onChange={(opt) => onFieldChange('menuHeight', `${opt[0]}px`, 'menu')}
              />
            </div>
          </Field>
        )}
        {!isPhonePortrait && (
          <>
            {(isNavPositionLeading || isNavigationBarTop) && (
              <Field isVertical className='py-0 flex items-center!'>
                <div className='flex items-center justify-between w-full'>
                  <label>
                    <Trans>Width</Trans>
                  </label>
                  <p className='text-xs text-light flex justify-end'>{menuWidth}</p>
                </div>
                <div className='w-full flex flex-col gap-0'>
                  <SliderFieldSetting
                    id='menu-width'
                    settingId='menu-width'
                    value={menuWidth}
                    sliderSettings={{
                      max: 300,
                      min: 20,
                      step: 5,
                      unit: 'px',
                      showUnit: true,
                    }}
                    onChange={(value) => onFieldChange('menuWidth', value[0], 'menu')}
                  />
                </div>
              </Field>
            )}
          </>
        )}
        {isShowMaxItems && (
          <Field isVertical className='py-0 flex items-center!'>
            <div className='flex items-center justify-between w-full'>
              <label>
                <Trans>Max Items</Trans>
              </label>
              <p className='text-xs text-light flex justify-end'>{maxItems} items</p>
            </div>
            <div className='w-full flex flex-col gap-0'>
              <Slider
                values={[
                  isShowMaxItems
                    ? maxItems && maxItems > 10
                      ? 10
                      : maxItems && maxItems <= 10
                        ? maxItems
                        : validateSliderValue(maxItems ?? '', 0, 1000)
                    : validateSliderValue(maxItems ?? '', 0, 1000),
                ]}
                max={isShowMaxItems ? 10 : 1000}
                min={0}
                step={1}
                onChange={(value) => onFieldChange('maxItems', Number(value), 'menu')}
              />
            </div>
          </Field>
        )}
      </div>
      {!isPhonePortrait && (
        <>
          <div className='flex items-center justify-between mt-6'>
            <p className='text-xs font-medium text-teal-400'>LOGO</p>
            {ModeIcon}
          </div>
          <div className='flex flex-col gap-2'>
            <Field isVertical className='py-0 flex items-center!'>
              <label className='w-full'>
                <Trans>Logo</Trans>
              </label>
              <div className='w-full flex flex-col gap-0'>
                <SelectDropdown
                  key={refreshKey}
                  className='dark:bg-slate-700/90'
                  id='small-mobile-position'
                  options={brandLogos}
                  renderOption={(opt) => {
                    if (!opt) {
                      return noLogoOption
                    }
                    return (
                      <div className='flex items-center gap-1'>
                        {opt?.value ? (
                          <>
                            <img
                              className='w-6 h-auto rounded-md'
                              src={GetImageMetdataLink(opt?.value, ImageMetadata.XS)}
                              alt={opt?.label || 'logo'}
                            />
                            <p className='text-xs font-normal'>{opt?.label}</p>
                          </>
                        ) : (
                          noLogoOption
                        )}
                      </div>
                    )
                  }}
                  menuAfter={
                    <div className='flex flex-col gap-3 py-1'>
                      {noLogoOption}
                      {addNewLogoOption}
                    </div>
                  }
                  value={activeLogo}
                  onChange={(value) => onFieldChange(mode === 'dark' ? 'logoDark' : 'logo', value, 'logo')}
                />
              </div>
            </Field>
            <Field isVertical className='py-0 flex items-center!'>
              <label className='w-full'>
                <Trans>Corner Radius</Trans>
              </label>
              <div className='w-full flex flex-col gap-0'>
                <SelectDropdown
                  className='dark:bg-slate-700/90'
                  id='corner-radius'
                  options={Object.values(MdShape)}
                  renderOption={(opt: MdShape) => (
                    <div className='flex items-center gap-1'>
                      <p className='text-xs font-normal capitalize'>{opt}</p>
                    </div>
                  )}
                  value={(cornerRadius as MdShape) ?? MdShape.None}
                  onChange={(value) => onFieldChange('cornerRadius', value, 'logo')}
                />
              </div>
            </Field>
            {isNavPositionLeading && (
              <>
                <label className='w-full'>Logo Alignment</label>
                <div className='w-fit'>
                  <ToggleField
                    label=''
                    options={[
                      { title: 'Leading', name: 'leading', icon: <Icon.AlignLeft16 /> },
                      { title: 'Center', name: 'center', icon: <Icon.AlignCenter16 /> },
                      { title: 'Trailing', name: 'trailing', icon: <Icon.AlignRight16 /> },
                    ]}
                    selected={logoAlignment ?? 'leading'}
                    onSelect={(name) => onFieldChange('logoAlignment', name, 'logo')}
                  />
                </div>
              </>
            )}
            <Field isVertical className='py-0 flex items-center! -mt-2'>
              <label className='w-full -mb-4'>
                <Trans>Logo Width</Trans>
              </label>
              <div className='w-full flex flex-col gap-0'>
                <p className='text-xs text-light flex justify-end'>{logoWidth}</p>
                <Slider
                  values={[validateSliderValue(logoWidth ?? '', 50, 300)]}
                  step={5}
                  min={50}
                  max={300}
                  onChange={(opt) => onFieldChange('logoWidth', `${opt[0]}px`, 'logo')}
                />
              </div>
            </Field>
          </div>
        </>
      )}

      <p className='text-xs font-medium mt-4 text-teal-400'>SHOW/HIDE</p>
      {filteredSwitchFields.map(({ label, name, checked }) => {
        const setting = findKeyByValue(navbarConfigGroups, name)
        return (
          <Field key={label} isVertical className='py-0 flex items-center! flex-row! justify-between! min-h-10!'>
            <label>{label}</label>
            <Switch checked={checked} onClick={(e) => onSwitchChange(name, e, setting)} />
          </Field>
        )
      })}
      <button
        className={clsx(
          'text-gray-100 text-xs py-1 w-full rounded-md transition-colors my-4',
          isApplying ? 'bg-primary-700 cursor-wait' : 'bg-primary-500 hover:bg-primary-600'
        )}
        onClick={handleApplyAllChanges}
        disabled={isApplying}
      >
        {isApplying ? 'Saving changes...' : 'Save changes to all'}
      </button>
    </>
  )
}
